import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { SupportReportMissedByHourItem } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);
const WEEKDAYS = Array.from({ length: 7 }, (_, weekday) => weekday);
// 2024-01-01 was a Monday, so day n of that week names weekday n (0 is Monday).
const mondayPlus = (days: number) => new Date(Date.UTC(2024, 0, 1 + days));

export interface MissedHeatmapProps {
  cells: readonly SupportReportMissedByHourItem[];
  timezone: string;
}

/** Missed chats by local weekday and hour, the darker the more: where staffing falls short. */
export function MissedHeatmap({ cells, timezone }: MissedHeatmapProps) {
  const { t, i18n } = useTranslation('reports');
  const format = useFormat();
  const counts = useMemo(
    () => new Map(cells.map((c) => [`${c.weekday}:${c.hour}`, c.count])),
    [cells],
  );
  const max = Math.max(0, ...cells.map((c) => c.count));
  const weekday = useMemo(() => {
    const short = new Intl.DateTimeFormat(i18n.language, { weekday: 'short', timeZone: 'UTC' });
    const long = new Intl.DateTimeFormat(i18n.language, { weekday: 'long', timeZone: 'UTC' });
    return {
      short: (d: number) => short.format(mondayPlus(d)),
      long: (d: number) => long.format(mondayPlus(d)),
    };
  }, [i18n.language]);
  const hourLabel = (hour: number) => `${String(hour).padStart(2, '0')}:00`;

  if (cells.length === 0) {
    return (
      <p className="text-muted-foreground py-6 text-center text-sm">{t('support.missed.none')}</p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="max-w-full overflow-x-auto">
        <table
          className="border-separate border-spacing-0.5"
          aria-label={t('support.missed.title')}
        >
          <thead>
            <tr>
              <td />
              {HOURS.map((hour) => (
                <th
                  key={hour}
                  scope="col"
                  className="text-muted-foreground w-5 text-center text-[10px] font-normal"
                >
                  <span aria-hidden>{hour % 3 === 0 ? hour : ''}</span>
                  <span className="sr-only">{hourLabel(hour)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {WEEKDAYS.map((day) => (
              <tr key={day}>
                <th
                  scope="row"
                  className="text-muted-foreground pr-2 text-left text-xs font-normal"
                >
                  <abbr title={weekday.long(day)} className="no-underline">
                    {weekday.short(day)}
                  </abbr>
                </th>
                {HOURS.map((hour) => {
                  const count = counts.get(`${day}:${hour}`) ?? 0;
                  return (
                    <td
                      key={hour}
                      title={t('support.missed.cell', {
                        day: weekday.long(day),
                        hour: hourLabel(hour),
                        count,
                      })}
                      className="bg-muted relative size-5 overflow-hidden rounded-sm p-0"
                    >
                      {count > 0 ? (
                        <span
                          className="bg-chart-4 absolute inset-0"
                          style={{ opacity: 0.25 + 0.75 * (count / max) }}
                          aria-hidden
                        />
                      ) : null}
                      <span className="sr-only">{format.number(count)}</span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-muted-foreground text-xs">{t('support.missed.timezone', { timezone })}</p>
    </div>
  );
}
